// @ts-nocheck
import {
  Card,
  Code,
  ColorSchemeSwitcher,
  Divider,
  FontSizeSwitcher,
  Group,
  LanguageSwitcher,
  SimpleGrid,
  Stack,
  Text,
  ThemeSwitcher,
  Title,
} from '@/components/ui';

export default function SwitchersPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Switchers</Title>
        <Text c="dimmed" size="sm">
          Preference controls for toggling theme mode, color scheme, language, and font size. Each
          switcher reads and writes the shared preferences store, so changes apply live across the
          app.
        </Text>
      </Stack>

      {/* ThemeSwitcher Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ThemeSwitcher</Title>
            <Text size="sm" c="dimmed">
              Toggle between light, dark, and auto color modes. Available as an icon button, a
              switch, or a segmented control.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">icon (default)</Text>
                <ThemeSwitcher variant="icon" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">switch</Text>
                <ThemeSwitcher variant="switch" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">segmented</Text>
                <ThemeSwitcher variant="segmented" showLabel />
              </Stack>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ThemeSwitcher variant="icon" />
<ThemeSwitcher variant="switch" />
<ThemeSwitcher variant="segmented" showLabel />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'icon' | 'switch' | 'segmented'
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'
              <br />
              • showLabel - boolean, show text labels on the segmented variant
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ColorSchemeSwitcher Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ColorSchemeSwitcher</Title>
            <Text size="sm" c="dimmed">
              Pick the active color scheme/palette. Renders as a searchable select, a swatch grid,
              scrollable pills, or a compact row of color dots.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Select (default):</Text>
            <ColorSchemeSwitcher variant="select" />
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Compact:</Text>
            <ColorSchemeSwitcher variant="compact" />
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Pills:</Text>
            <ColorSchemeSwitcher variant="pills" maxHeight={120} />
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Grid:</Text>
            <ColorSchemeSwitcher variant="grid" columns={3} maxHeight={260} />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ColorSchemeSwitcher variant="select" />
<ColorSchemeSwitcher variant="compact" />
<ColorSchemeSwitcher variant="pills" maxHeight={120} />
<ColorSchemeSwitcher variant="grid" columns={3} maxHeight={260} />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'select' | 'grid' | 'pills' | 'compact'
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'
              <br />
              • columns - number of columns in the grid variant (default 5)
              <br />
              • maxHeight - scroll height for grid/pills variants (default 300)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* LanguageSwitcher Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>LanguageSwitcher</Title>
            <Text size="sm" c="dimmed">
              Switch the active language/locale. Available as a searchable select, a dropdown menu,
              or a segmented control of short codes.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">select (default)</Text>
                <LanguageSwitcher variant="select" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">menu</Text>
                <LanguageSwitcher variant="menu" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">segmented</Text>
                <LanguageSwitcher variant="segmented" />
              </Stack>
            </SimpleGrid>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <Text size="sm" fw={500}>Without flags:</Text>
            <LanguageSwitcher variant="select" showFlag={false} />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<LanguageSwitcher variant="select" />
<LanguageSwitcher variant="menu" />
<LanguageSwitcher variant="segmented" />
<LanguageSwitcher variant="select" showFlag={false} />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'select' | 'menu' | 'segmented'
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'
              <br />
              • showFlag - boolean, prefix labels with the country flag (default true)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* FontSizeSwitcher Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>FontSizeSwitcher</Title>
            <Text size="sm" c="dimmed">
              Adjust the application font size. Available as increment/decrement icon buttons, a
              segmented control, or a slider with marks.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Variants:</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">icons</Text>
                <FontSizeSwitcher variant="icons" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">segmented (default)</Text>
                <FontSizeSwitcher variant="segmented" />
              </Stack>
              <Stack gap={6} align="flex-start">
                <Text size="xs" c="dimmed">slider</Text>
                <FontSizeSwitcher variant="slider" showLabel />
              </Stack>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<FontSizeSwitcher variant="icons" />
<FontSizeSwitcher variant="segmented" />
<FontSizeSwitcher variant="slider" showLabel />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • variant - 'icons' | 'segmented' | 'slider'
              <br />
              • size - 'xs' | 'sm' | 'md' | 'lg'
              <br />
              • showLabel - boolean, show descriptive labels on segmented/slider variants
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Usage Note */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>State Management</Title>
            <Text size="sm" c="dimmed">
              All switchers integrate with the shared preferences store for persistent settings, so
              they stay in sync wherever they are rendered.
            </Text>
          </Stack>

          <Code block language="tsx">
{`import { usePreferencesStore } from '@/stores';

// Read current values
const { colorSchemeMode, colorSchemeKey, language, fontSize } =
  usePreferencesStore();

// Update programmatically
const setColorSchemeMode = usePreferencesStore((s) => s.setColorSchemeMode);
setColorSchemeMode('dark');`}
          </Code>
        </Stack>
      </Card>
    </Stack>
  );
}
